export type Partner = string; // 이제 동적으로 결정되므로 string으로 변경

export interface PartnerInfo {
  title: string;
  value: Partner;
  pattern: string;
}

// API에서 파트너 정보를 가져와서 PartnerInfo 형태로 변환하는 함수
export const createPartnerInfoFromApi = (
  apiPartners: Array<{ companyName: string; code: string; topLevelDomain?: string }>,
): PartnerInfo[] => {
  return apiPartners.map((partner) => ({
    title: partner.companyName,
    value: partner.code,
    pattern: partner.topLevelDomain || partner.code, // topLevelDomain이 있으면 사용, 없으면 code 사용
  }));
};

// 기존 하드코딩된 파트너 정보 (fallback용)
const FALLBACK_PARTNER_MAP: PartnerInfo[] = [
  { title: 'SAVETAX', value: 'savetax', pattern: 'save-tax' },
  { title: '지우회계법인', value: 'jiwoo', pattern: 'jiwoo' },
  { title: '세무법인로움', value: 'loum', pattern: 'loum' },
  { title: '예인더케이', value: 'thek', pattern: 'thek' },
  { title: '성현회계법인', value: 'bdo', pattern: 'bdo' },
  { title: '새로회계법인', value: 'saero', pattern: 'saero' },
  { title: '더클회계법인', value: 'theclacc', pattern: 'theclacc' },
  { title: '세무법인엑스퍼트', value: 'expert', pattern: 'expert' },
  { title: '그로스파이낸스', value: 'growthfinance', pattern: 'growthfinance' },
  { title: '세무법인아크로', value: 'acro', pattern: 'acro' },
];

// 오버로드: hostname만 전달하는 경우 (fallback 사용)
export function getPartnerByHostname(hostname: string | undefined): PartnerInfo | null;
// 오버로드: hostname과 partnerMap을 전달하는 경우
export function getPartnerByHostname(
  hostname: string | undefined,
  partnerMap: PartnerInfo[],
): PartnerInfo | null;
// 구현
export function getPartnerByHostname(
  hostname: string | undefined,
  partnerMap?: PartnerInfo[],
): PartnerInfo | null {
  if (!hostname) return null;

  const map = partnerMap || FALLBACK_PARTNER_MAP;
  if (!map || !map.length) return null;

  // hostname에서 맨 앞 부분 추출 (예: a.b.c.d -> a, x.y.z -> x)
  const topLevelDomain = hostname.split('.')[0];

  // pattern이 topLevelDomain과 정확히 일치하는 파트너 찾기
  return map.find((partner) => partner.pattern === topLevelDomain) ?? null;
}

// 오버로드: code만 전달하는 경우 (fallback 사용)
export function getPartnerByCode(code: string): PartnerInfo | undefined;
// 오버로드: code와 partnerMap을 전달하는 경우
export function getPartnerByCode(code: string, partnerMap: PartnerInfo[]): PartnerInfo | undefined;
// 구현
export function getPartnerByCode(
  code: string,
  partnerMap?: PartnerInfo[],
): PartnerInfo | undefined {
  const map = partnerMap || FALLBACK_PARTNER_MAP;
  if (!map || !map.length) return undefined;
  return map.find((partner) => partner.value === code);
}

/**
 * 도메인 주소에서 파트너 도메인(서브도메인)을 추출하는 함수
 * @param hostname - 전체 도메인 주소 (예: 'kb.finance.cloud', 'savetax.finance.cloud')
 * @returns 파트너 도메인 (예: 'kb', 'savetax') 또는 null
 */
export function extractPartnerDomain(hostname: string | undefined): string | undefined {
  if (!hostname) return undefined;

  // finance.cloud가 포함된 경우에만 파트너 도메인 추출
  if (!hostname.includes('finance.cloud')) {
    return undefined;
  }

  // hostname에서 맨 앞 부분 추출 (예: kb.finance.cloud -> kb, savetax.finance.cloud -> savetax)
  const parts = hostname.split('.');

  // 도메인이 하나뿐이거나 유효하지 않으면 null 반환
  if (parts.length <= 1) return undefined;

  return parts[0];
}
